import { DwQueryRequest, SortDir } from '../../CustomDashboards/types';
import { EntityTypes, FilterContext } from './types';
import { createExpressionArrayFromFilters } from './utils';

export const getTotalAlerts = (filterContext: FilterContext): DwQueryRequest => ({
  select: {
    metrics: [
      {
        name: 'active_alerts_granular',
        alias: 'value',
      },
    ],
    dims: [],
  },
  sorting: [],
  projectionId: {
    name: 'uber_alerts',
    builtIn: true,
  },
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'alert.first_seen',
            dateCondition: { between: { values: filterContext.dateRange } },
          },
        },
        ...createExpressionArrayFromFilters(filterContext),
      ],
    },
  },
});

export const getTotalIncident = (filterContext: FilterContext): DwQueryRequest => ({
  select: {
    metrics: [
      {
        name: 'active_incidents_granular',
        alias: 'value',
      },
    ],
    dims: [],
  },
  sorting: [],
  projectionId: {
    name: 'incidents',
    builtIn: true,
  },
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'incident.first_seen',
            dateCondition: { between: { values: filterContext.dateRange } },
          },
        },
        ...createExpressionArrayFromFilters(filterContext),
      ],
    },
  },
});

export const getCommonAlerts = (filterContext: FilterContext): DwQueryRequest => ({
  select: {
    metrics: [
      {
        name: 'active_alerts_granular',
        alias: 'value',
      },
    ],
    dims: [
      {
        name: 'alert.title',
        alias: 'title',
      },
    ],
  },
  projectionId: {
    name: 'uber_alerts',
    builtIn: true,
  },
  groupBy: ['alert.title'],
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'alert.first_seen',
            dateCondition: { between: { values: filterContext.dateRange } },
          },
        },
        ...createExpressionArrayFromFilters(filterContext),
      ],
    },
  },
  sorting: [{ dir: SortDir.DESC, name: 'value' }],
  top: {
    size: 5,
    groupOthers: false,
  },
});

export const getNovelAlerts = (filterContext: FilterContext): DwQueryRequest => {
  const { dateRange } = filterContext;
  const startDate = dateRange[0].toISOString().replace(/\.\d{3}Z$/, '');
  const endDate = dateRange[1].toISOString().replace(/\.\d{3}Z$/, '');
  const operands = createExpressionArrayFromFilters(filterContext);
  const filterReq = operands.length
    ? {
        filter: {
          and: {
            operands,
          },
        },
      }
    : {};

  return {
    ...filterReq,
    select: {
      dims: [
        {
          name: `COUNT_DISTINCT_IF(alert.title, alert.state = 'ACTIVE' and alert.first_seen >= '${startDate}' and alert.first_seen <= '${endDate}' and alert.title NOT IN(COLLECT_DISTINCT_IF(alert.title, alert.first_seen < '${startDate}')))`,
          alias: 'value',
        },
      ],
      metrics: [],
    },
    groupBy: [],
    sorting: [],
    projectionId: {
      name: 'uber_alerts',
      builtIn: true,
    },
    distinct: false,
  };
};

export const getIncidentStatus = (filterContext: FilterContext): DwQueryRequest => ({
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'incident.first_seen',
            dateCondition: { between: { values: filterContext.dateRange } },
          },
        },
        ...createExpressionArrayFromFilters(filterContext),
      ],
    },
  },
  select: {
    dims: [
      {
        name: 'incident.current_status.name',
      },
    ],
    metrics: [
      {
        name: 'active_incidents_granular',
        alias: 'active_incidents_granular',
      },
    ],
  },
  groupBy: ['incident.current_status.name'],
  sorting: [
    {
      name: 'active_incidents_granular',
      dir: SortDir.DESC,
    },
  ],
  top: {
    size: 5,
    groupOthers: false,
  },
  projectionId: {
    name: 'incidents',
    builtIn: true,
  },
  distinct: false,
});

export const getTop5VendorsByAlerts = (filterContext: FilterContext): DwQueryRequest => ({
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'alert.first_seen',
            dateCondition: { between: { values: filterContext.dateRange } },
          },
        },
        ...createExpressionArrayFromFilters(filterContext),
      ],
    },
  },
  groupBy: ['alert.source_names'],
  distinct: false,
  metricsFilterThreshold: 0,
  select: {
    metrics: [
      {
        name: 'active_alerts_granular',
        alias: 'active_alerts_granular',
      },
    ],
    dims: [
      {
        name: 'alert.source_names',
        alias: 'source_names',
      },
    ],
  },
  sorting: [],
  top: {
    size: 5,
    groupOthers: false,
    offset: 0,
  },
  projectionId: {
    name: 'uber_alerts',
    builtIn: true,
  },
});

export const getTop5VendorsByIncidents = (filterContext: FilterContext): DwQueryRequest => ({
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'incident.first_seen',
            dateCondition: { between: { values: filterContext.dateRange } },
          },
        },
        ...createExpressionArrayFromFilters(filterContext),
      ],
    },
  },
  groupBy: ['incident.source_names'],
  distinct: false,
  metricsFilterThreshold: 0,
  select: {
    metrics: [
      {
        name: 'active_incidents_granular',
        alias: 'active_incidents_granular',
      },
    ],
    dims: [
      {
        name: 'incident.source_names',
        alias: 'source_names',
      },
    ],
  },
  sorting: [],
  top: {
    size: 5,
    groupOthers: false,
    offset: 0,
  },
  projectionId: {
    name: 'incidents',
    builtIn: true,
  },
});

export const getTop5UsersByIncidents = (ids: string[], filterContext: FilterContext): DwQueryRequest => {
  const userKeyFilters: any[] = [];
  if (ids.length !== 0) {
    userKeyFilters.push({
      expression: {
        fieldName: 'user._key',
        stringCondition: {
          in: {
            values: ids,
          },
        },
      },
    });
  }

  return {
    filter: {
      and: {
        operands: [
          {
            expression: {
              fieldName: 'user.email',
              stringCondition: {
                notEmpty: {},
                addNullFilter: false,
              },
            },
          },
          {
            expression: {
              fieldName: 'incident.first_seen',
              dateCondition: { between: { values: filterContext.dateRange } },
            },
          },
          ...userKeyFilters,
          ...createExpressionArrayFromFilters(filterContext),
        ],
      },
    },
    groupBy: ['user.email'],
    distinct: false,
    metricsFilterThreshold: 0,
    select: {
      metrics: [
        {
          name: 'active_incidents_granular',
          alias: 'value',
        },
      ],
      dims: [
        {
          name: 'user.email',
          alias: 'name',
        },
        {
          name: 'user._key',
          alias: 'entityId',
        },
        {
          name: `'${EntityTypes.USER}'`,
          alias: 'type',
        },
      ],
    },
    sorting: [
      {
        name: 'value',
        dir: SortDir.DESC,
      },
      {
        name: 'name',
        dir: SortDir.DESC,
      },
    ],
    top: {
      size: 5,
      groupOthers: false,
      offset: 0,
    },
    projectionId: {
      name: 'incidents',
      builtIn: true,
    },
  };
};

export const getTop5AssetByIncidents = (ids: string[], filterContext: FilterContext): DwQueryRequest => {
  const assetKeyFilters: any[] = [];
  if (ids.length !== 0) {
    assetKeyFilters.push({
      expression: {
        fieldName: 'asset._key',
        stringCondition: {
          in: {
            values: ids,
          },
        },
      },
    });
  }
  return {
    filter: {
      and: {
        operands: [
          {
            expression: {
              fieldName: 'asset.name',
              stringCondition: {
                notEmpty: {},
                addNullFilter: false,
              },
            },
          },
          {
            expression: {
              fieldName: 'incident.first_seen',
              dateCondition: { between: { values: filterContext.dateRange } },
            },
          },
          ...assetKeyFilters,
          ...createExpressionArrayFromFilters(filterContext),
        ],
      },
    },
    groupBy: ['asset.name'],
    distinct: false,
    metricsFilterThreshold: 0,
    select: {
      metrics: [
        {
          name: 'active_incidents_granular',
          alias: 'value',
        },
      ],
      dims: [
        {
          name: 'asset.name',
          alias: 'name',
        },
        {
          name: 'asset._key',
          alias: 'entityId',
        },
        {
          name: `'${EntityTypes.ASSET}'`,
          alias: 'type',
        },
      ],
    },
    sorting: [
      {
        name: 'value',
        dir: SortDir.DESC,
      },
      {
        name: 'name',
        dir: SortDir.DESC,
      },
    ],
    top: {
      size: 5,
      groupOthers: false,
      offset: 0,
    },
    projectionId: {
      name: 'incidents',
      builtIn: true,
    },
  };
};

export const getAlertsMappedToTactic = (filterContext: FilterContext): DwQueryRequest => ({
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'alert.first_seen',
            dateCondition: { between: { values: filterContext.dateRange } },
          },
        },
        ...createExpressionArrayFromFilters(filterContext),
      ],
    },
  },
  groupBy: ['mitre_attack.tactic'],
  select: {
    metrics: [
      {
        name: 'active_alerts_granular',
        alias: 'active_alerts_granular',
      },
    ],
    dims: [
      {
        name: 'mitre_attack.tactic',
      },
    ],
  },
  sorting: [
    {
      name: 'active_alerts_granular',
      dir: SortDir.DESC,
    },
  ],
  projectionId: {
    name: 'uber_alerts',
    builtIn: true,
  },
});

export const getTop5HighRiskIncidents = (filterContext: FilterContext): DwQueryRequest => ({
  select: {
    metrics: [
      {
        name: 'max_incident_risk_mass',
        alias: 'max_incident_risk_mass',
      },
    ],
    dims: [
      {
        name: 'incident.title',
        alias: 'title',
      },
      {
        name: 'incident._key',
        alias: 'key',
      },
      {
        name: 'incident.source_names',
        alias: 'vendors',
      },
      {
        name: 'incident.sla',
        alias: 'sla',
      },
      { name: 'incident.user_keys', alias: 'users' },
      { name: 'incident.asset_keys', alias: 'assets' },
    ],
  },
  projectionId: {
    name: 'incidents',
    builtIn: true,
  },
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'incident.first_seen',
            dateCondition: { between: { values: filterContext.dateRange } },
          },
        },
        ...createExpressionArrayFromFilters(filterContext),
      ],
    },
  },
  groupBy: ['incident.title', 'incident._key', 'alert.vendor', 'incident.sla'],
  sorting: [
    {
      name: 'max_incident_risk_mass',
      dir: SortDir.DESC,
    },
  ],
  top: {
    size: 5,
    groupOthers: false,
    offset: 0,
  },
});

export const getMeanTimeToRespond = (filterContext: FilterContext): DwQueryRequest => {
  const operands = createExpressionArrayFromFilters(filterContext);
  const filter = operands.length
    ? {
        filter: {
          and: {
            operands,
          },
        },
      }
    : {};

  return {
    groupBy: ['BUCKET_MONTH'],
    distinct: false,
    ...filter,
    metricsFilterThreshold: 0,
    select: {
      metrics: [
        {
          name: 'incident_mttrespond',
          alias: 'incident_mttrespond',
        },
      ],
      dims: [
        {
          name: 'BUCKET_MONTH',
        },
      ],
    },
    sorting: [
      {
        name: 'BUCKET_MONTH',
        dir: SortDir.ASC,
      },
    ],
    top: {
      size: 6,
      groupOthers: false,
      offset: 0,
    },
    projectionId: {
      name: 'incidents',
      builtIn: true,
    },
    timeRange: {
      between: {
        values: filterContext.dateRange,
      },
    },
    timezoneOffset: -120,
    timezone: 'Asia/Jerusalem',
  };
};

export const getIncidentTable = (filterContext: FilterContext, page, pageSize): DwQueryRequest => ({
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'incident.first_seen',
            dateCondition: { between: { values: filterContext.dateRange } },
          },
        },
        {
          expression: {
            fieldName: 'incident.state',
            stringCondition: {
              in: {
                values: ['ACTIVE'],
              },
            },
          },
        },
        ...createExpressionArrayFromFilters(filterContext),
      ],
    },
  },
  select: {
    dims: [
      {
        name: 'incident._key',
        alias: 'key',
      },
      {
        name: 'incident.title',
      },
      {
        name: 'incident.first_seen',
      },
      {
        name: 'incident.source_names',
      },
      {
        name: 'incident.type',
      },
    ],
    metrics: [
      {
        name: 'max_incident_risk_mass',
        alias: 'risk_mass',
      },
    ],
  },
  sorting: [{ name: 'risk_mass', dir: SortDir.DESC }],

  top: {
    size: pageSize,
    offset: page * pageSize,
    groupOthers: false,
  },
  projectionId: {
    name: 'incidents',
    builtIn: true,
  },
});

export const getAlertTable = (filterContext: FilterContext, page, pageSize): DwQueryRequest => ({
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'alert.first_seen',
            dateCondition: { between: { values: filterContext.dateRange } },
          },
        },
        ...createExpressionArrayFromFilters(filterContext),
      ],
    },
  },
  select: {
    dims: [
      {
        name: 'alert._key',
        alias: 'key',
      },
      {
        name: 'alert.title',
      },
      {
        name: 'alert.severity_score',
      },
      {
        name: 'alert.first_seen',
      },
      {
        name: 'alert.user_keys',
      },
      {
        name: 'alert.asset_keys',
      },
      {
        name: 'mitre_attack.technique',
      },
    ],
    metrics: [{ name: 'active_incidents_granular', alias: 'total_incidents' }],
  },
  sorting: [{ name: 'alert.severity_score', dir: SortDir.DESC }],
  top: {
    size: pageSize,
    offset: page * pageSize,
    groupOthers: false,
  },
  projectionId: {
    name: 'uber_alerts',
    builtIn: true,
  },
});
